import { useEffect, useRef, useState } from "react";

import type { Provider } from "~/api/providers";
import { MainLayout } from "~/layout/MainLayout";
import { AppointmentCreatedIcon } from "~/shared/components/icons/AppointmentCreatedIcon";
import {
  formatAppointmentDate,
  formatAppointmentTimeWithTimezone,
} from "~/shared/utils/dates";
import { useSignUpStore } from "~/stores";
import { useFunnelStore } from "~/stores/useFunnelStore";
import { FormContentContainer } from "../components/SignUp";
import { shouldFirePixelEvents, trackGA4CompleteRegistration } from "../utils";
import { clearUtmParams, getUtmParams } from "../utils/utmCapture";

const isInternal = window.location.hostname.startsWith("internal.");
const WEBSITE_URL = isInternal
  ? "https://internal.app.sarahlynnnutrition.com/sign-up"
  : "https://www.sarahlynnnutrition.com/";

interface FormProps {
  provider?: Provider;
  appointmentDateTime?: string;
  location?: string | null;
  appointmentId?: string | null;
}

export const AppointmentCreatedScreen = ({
  provider,
  appointmentDateTime,
  location,
  appointmentId,
}: FormProps) => {
  const formattedDate = appointmentDateTime
    ? formatAppointmentDate(appointmentDateTime)
    : "Date not available";
  const formattedTime = appointmentDateTime
    ? formatAppointmentTimeWithTimezone(appointmentDateTime)
    : "Time not available";
  const providerName = provider?.fullName.split(" ")[0];

  const { patient } = useSignUpStore();
  const isEmployerPPO =
    patient.primaryInsurance.planTypeProvider === "employer" &&
    patient.primaryInsurance.planCoverage === "PPO";

  const hasTrackedRef = useRef(false);
  const [showReturnLink, setShowReturnLink] = useState(false);

  // Fade in the return link after 4 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowReturnLink(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (hasTrackedRef.current) {
      return;
    }

    // Check parent window first (for iframe), then current window
    let fbq = null;
    const utm = getUtmParams();

    // Check subdomain before firing
    if (!shouldFirePixelEvents()) {
      hasTrackedRef.current = true;
      clearUtmParams();
      return;
    }

    try {
      if (window.parent && window.parent.fbq) {
        fbq = window.parent.fbq;
      }
    } catch (e) {
      console.log("Cross-origin access blocked, trying current window");
    }

    if (!fbq && window.fbq) {
      fbq = window.fbq;
    }

    if (fbq && isEmployerPPO) {
      // Get stored UTM params
      (fbq as any)(
        "track",
        "CompleteRegistration",
        {
          value: 0.0,
          currency: "USD",
          content_name: "Appointment Booking Complete",
          // Include UTM params if available
          ...(utm?.utm_source && { utm_source: utm.utm_source }),
          ...(utm?.utm_medium && { utm_medium: utm.utm_medium }),
          ...(utm?.utm_campaign && { utm_campaign: utm.utm_campaign }),
          ...(utm?.utm_term && { utm_term: utm.utm_term }),
          ...(utm?.utm_content && { utm_content: utm.utm_content }),
          // Include Meta ad params with Meta's naming convention
          ...(utm?.ad_set_id && { adset_id: utm.ad_set_id }),
          ...(utm?.ad_set_name && { adset_name: utm.ad_set_name }),
          ...(utm?.ad_id && { ad_id: utm.ad_id }),
          ...(utm?.ad_name && { ad_name: utm.ad_name }),
        },
        {
          eventID: appointmentId,
        },
      );
    } else if (!fbq) {
      console.log("window.fbq is NOT available in either window");
    }

    trackGA4CompleteRegistration({
      transaction_id: appointmentId,
    });

    hasTrackedRef.current = true;
    sessionStorage.removeItem("signup-form-storage");
    useFunnelStore.getState().resetSession();
  }, []);

  const getAddress = (location: string): string => {
    switch (location) {
      case "In person at Buffalo":
        return "In person at 5360 Genesee Street, Lancaster, NY 14026";
      case "In person at Albany":
        return "In person at 125 Wolf Road Suite 212 Albany, NY 12205";
      case "In person at Rochester":
        return "In person at 1160K Pittsford-Victor Road, 2nd Floor #4, Pittsford, NY 14534";
      case "In person at Syracuse":
        return "In person at 5100 West Genesee Street Camillus NY, 13031";
      default:
        return "Online";
    }
  };

  return (
    <MainLayout>
      <FormContentContainer
        className="flex flex-col items-center rounded-lg shadow-xl"
        showProgress={false}
      >
        <AppointmentCreatedIcon className="mb-4 size-16 self-center" />
        <div className="flex flex-col items-center text-center">
          <h1 className="font-display mb-2 text-xl">
            Appointment confirmed with {providerName}!
          </h1>
          <p className="mb-10 text-gray-400">
            Please check your email for your appointment details
            {location === "via Telehealth" && ","}
            <span>
              {location === "via Telehealth" && " including the Zoom link"}.
            </span>
          </p>
          <div className="mx-auto mb-10 w-full max-w-sm rounded-xl border border-gray-200 bg-gray-50/50 p-5">
            {/* Provider info - centered */}
            <div className="mb-4 flex flex-col items-center">
              {provider?.avatarUrl ? (
                <img
                  src={provider.avatarUrl}
                  alt={`${provider.fullName} profile`}
                  className="mb-2 size-14 rounded-full object-cover ring-2 ring-white"
                />
              ) : (
                <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-green-100 text-green-700 ring-2 ring-white">
                  <span className="font-display text-xl">
                    {provider?.fullName ? provider.fullName.charAt(0) : "P"}
                  </span>
                </div>
              )}
              <h2 className="font-display text-lg text-gray-900">
                {provider?.fullName}
              </h2>
            </div>

            {/* Appointment details - structured rows */}
            <div className="space-y-2 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Date</span>
                <span className="text-sm font-medium text-gray-900">
                  {formattedDate}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Time</span>
                <span className="text-sm font-medium text-gray-900">
                  {formattedTime}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Location</span>
                <span className="text-sm font-medium text-gray-900">
                  {location === "via Telehealth"
                    ? "Telehealth (Zoom)"
                    : getAddress(location ?? "").replace("In person at ", "")}
                </span>
              </div>
            </div>
          </div>

          {/* Return to website link - fades in after 4 seconds */}
          <a
            href={WEBSITE_URL}
            onClick={
              isInternal
                ? (e) => {
                    e.preventDefault();
                    sessionStorage.removeItem("signup-form-storage");
                    useSignUpStore.getState().reset();
                    useFunnelStore.getState().resetSession();
                    window.location.href = WEBSITE_URL;
                  }
                : undefined
            }
            className={`hover:bg-green-250 inline-flex items-center gap-2 rounded-lg bg-green-300 px-6 py-3 font-medium text-black transition-opacity duration-700 ${
              showReturnLink ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            {isInternal
              ? "Book Another Patient"
              : "Return to Sarah Lynn Nutrition"}
            <svg
              className="size-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
              />
            </svg>
          </a>
        </div>
      </FormContentContainer>
    </MainLayout>
  );
};
